import { _ } from "../../polyglot/polyglot.js";
import { makeNodeActive, renderContentOn } from "./utils.js";

/**
 * Make a searchable field for the select element.
 * This is solely made for internal use.
 */
export function makeSearchableField({
	allItems,
	selectedItem,
	searchFn,
	debouncedPerformSearch,
	buttonRef,
	onBlur,
	isOpen,
	onButtonClick,
}) {
	const div = document.createElement("div");
	div.classList.add("filter-input-container");

	const input = document.createElement("input");
	input.classList.add("filter-input");
	input.setAttribute("placeholder", _("Search…").toString());
	input.setAttribute("type", "search");

	input.addEventListener("click", (event) => {
		event.stopPropagation();
	});

	input.addEventListener("blur", (event) => {
		if (event.relatedTarget && buttonRef.contains(event.relatedTarget)) {
			return;
		}
		onBlur();
	});

	input.addEventListener("keyup", (event) => {
		debouncedPerformSearch({
			searchFn,
			key: event.target.value,
			allItems,
			selectedItem,
		});
	});

	input.addEventListener("keydown", (event) => {
		if (event.key === "ArrowUp" || event.key === "ArrowDown") {
			buttonRef.focus();
			return;
		}

		event.stopPropagation();

		if (event.key === "Enter") {
			event.preventDefault();
			return;
		}

		if (event.key !== "Escape") {
			return;
		}
		if (isOpen.current) {
			onButtonClick();
			buttonRef.focus();
		}
	});

	div.appendChild(input);
	return div;
}

/**
 * Make a node from an item to be used in the select element.
 * This is solely made for internal use.
 */
export function makeNode({
	item,
	selectedItem,
	onItemClick,
	selectContainerRef,
	itemToNode,
	nodeToItem,
	disabled = false,
	level = 1,
}) {
	if (isGroup(item)) {
		return makeNodeFromGroup({
			group: item,
			selectedItem,
			onItemClick,
			selectContainerRef,
			itemToNode,
			nodeToItem,
			disabled,
			level,
		});
	}

	return makeNodeFromOption({
		option: item,
		selectedItem,
		onItemClick,
		itemToNode,
		nodeToItem,
		disabled,
	});
}

function makeNodeFromOption({
	option,
	selectedItem,
	onItemClick,
	itemToNode,
	nodeToItem,
	disabled,
}) {
	const li = document.createElement("li");

	renderContentOn({ root: li, content: option.label });

	li.classList.add("option");

	if (selectedItem?.value === option.value) {
		li.classList.add("selected");
	}

	if (disabled || option.disabled) {
		li.setAttribute("disabled", "true");
	} else {
		li.setAttribute("tabindex", "1");
		li.addEventListener("click", onItemClick(option));

		li.addEventListener("mouseover", () => {
			makeNodeActive({ node: li, nodeToItem });
		});
	}

	itemToNode.set(option, li);
	nodeToItem.set(li, option);

	return li;
}

function makeNodeFromGroup({
	group,
	selectedItem,
	onItemClick,
	selectContainerRef,
	itemToNode,
	nodeToItem,
	disabled,
	level,
}) {
	const isDisabled = disabled || group.disabled;

	const label = document.createElement("span");

	renderContentOn({ root: label, content: group.label });

	const ul = document.createElement("ul");
	ul.setAttribute("data-level", level);

	group.options.forEach((option) => {
		const li = makeNode({
			item: option,
			onItemClick,
			selectedItem,
			selectContainerRef,
			itemToNode,
			nodeToItem,
			disabled: isDisabled,
			level: level + 1,
		});
		ul.appendChild(li);
	});

	const li = document.createElement("li");
	li.classList.add("group");
	li.appendChild(label);
	li.appendChild(ul);

	if (isDisabled) {
		li.setAttribute("disabled", "true");
	}

	itemToNode.set(group, li);
	nodeToItem.set(li, group);

	return li;
}

/**
 * Make a node to render when the filtre results in an empty list.
 * This is solely made for internal use.
 */
export function makeEmptyFilterNode() {
	const li = document.createElement("li");
	li.classList.add("empty-filter");
	renderContentOn({ root: li, content: _("Nothing found") });

	return li;
}

function isGroup(item) {
	return !!item.options;
}
